import type {
  ConfigFormValues,
  ExportConfigFormData,
  TableNameValidation,
} from '#lit/types/journeys.js';
import {
  CampaignTriggersExportActions,
  DestinationConfigFormField,
  ManualModeDirection,
} from '#lit/types/journeys.js';
import {JourneyStatus} from '#lit/common/constants/index.js';
import {locale} from '#lit/common/locale/index.js';
import {journeysLocale} from '#lit/common/locale/journeys.js';

import type {ManualModeRunParams} from '#lit/services/journeys.js';
import type {
  NodeMetric,
  NodeMetricStatus,
} from '#lit/types/builders/journey_builder/nodes.js';
import type {Nullable} from '#lit/types/general.js';
import type {IJourney} from '#lit/types/journey/index.js';
import type {TemplateResult} from 'lit';
import {html} from 'lit';
import {journeyStore} from '#stores/journey/index.js';
import {JourneyScheduleType} from '#lit/types/journey/schedule.js';
import {openModalEvent} from '#types/modals.js';
import type {DestinationConnection} from '#types/destinations.js';
import {
  ADS_REQUIRED_FIELDS,
  BASE_REQUIRED_FIELDS,
  CAMPAIGN_TRIGGERS_REQUIRED_FIELDS,
  CAMPAIGN_TRIGGERS_SEND_EMAIL_CHOOSE_SENDER_REQUIRED_FIELDS,
  CAMPAIGN_TRIGGERS_SEND_EMAIL_REQUIRED_FIELDS,
  DW_REQUIRED_FIELDS,
  EMAIL_REQUIRED_FIELDS,
  SENDABLE_EXPORT_ACTIONS,
  SFMC_ADD_TO_EXISTING_DATA_EXTENSION_REQUIRED_FIELDS,
} from '#lit/common/constants/journeys.js';
import {isUndefined} from './index.js';
import {ValidDestinationCategory} from '#lit/types/journey/destination_node.js';

export function getJourneyPath(journeyId: number, datasetGroupId: number) {
  return `/v2/dataset_groups/${datasetGroupId}/journeys/${journeyId}/edit`;
}

export function getNewBuilderJourneyPath(
  journeyId: number,
  datasetGroupId: number
) {
  return `/v2/dataset_groups/${datasetGroupId}/journeys/${journeyId}/edit`;
}

export enum AskForPii {
  YES = 'yes', // Ask for PII fields (to be used for Match Fields or Personalization/Additional Fields)
  NO = 'no', // Do not ask for PII fields
  IF_ALLOWED = 'if_allowed', // Ask for PII fields if user is allowed
}

export interface searchFieldsUrlParams {
  datasetGroupId: number | undefined;
  datasetId?: number;
  limit: number;
  query?: string;
  page?: number;
  pii?: AskForPii;
  includeSystemAndComputedFields?: boolean;
  queueEventID?: number;
}

export function searchFieldsUrl({
  datasetGroupId,
  datasetId,
  limit,
  query,
  page,
  pii,
  includeSystemAndComputedFields,
  queueEventID,
}: searchFieldsUrlParams) {
  if (typeof datasetGroupId === 'undefined' || datasetGroupId < 0) {
    console.error('dataset group ID must be greater than 0');
    throw new Error('dataset group ID must be greater than 0');
  }

  if (!datasetId || datasetId < 0) {
    console.error('dataset ID must be greater than 0');
    throw new Error('dataset ID must be greater than 0');
  }

  const endpoint = queueEventID
    ? `/api/v2/queue_events/${queueEventID}/field_search?limit=20&page=1`
    : `/api/v2/dataset_groups/${datasetGroupId}/field_search`;

  const url = new URL(endpoint, location.href);
  url.searchParams.append('dataset_id', String(datasetId));
  if (query) url.searchParams.append('query', query);
  if (location.href.includes('audiences')) {
    url.searchParams.append('fields_type', 'audience');
  }
  const audienceIdMatch = location.href.match(/audiences\/(\d+)/);
  const audienceId = audienceIdMatch ? audienceIdMatch[1] : '';
  url.searchParams.append('audience_id', audienceId);
  if (location.href.includes('journeys')) {
    url.searchParams.append('fields_type', 'journey');
  }
  const journeyIdMatch = location.href.match(/journeys\/(\d+)/);
  const journeyId = journeyIdMatch ? journeyIdMatch[1] : '';
  url.searchParams.append('journey_id', journeyId);
  url.searchParams.append('page', String(page));
  if (limit > 0) url.searchParams.append('limit', String(limit));
  switch (pii) {
    case AskForPii.YES:
      url.searchParams.append('include_pii', 'true');
      break;
    case AskForPii.IF_ALLOWED:
      url.searchParams.append('include_pii_if_allowed', 'true');
      break;
  }
  if (includeSystemAndComputedFields !== undefined) {
    url.searchParams.append(
      'include_system_and_computed_fields',
      includeSystemAndComputedFields.toString()
    );
  }
  url.searchParams.append('exclude_rejected_custom_fields', 'true');
  return url.toString();
}

export function flattenExportData(
  data: ExportConfigFormData
): ConfigFormValues {
  return {
    // Fields from ExportConfigFormData
    [DestinationConfigFormField.PLATFORM_TYPE]: data.platform_type,
    [DestinationConfigFormField.EXPORT_NAME]: data.exportName,
    [DestinationConfigFormField.DURATION]: data.duration,
    [DestinationConfigFormField.WHEN_TO_ADVANCE_USER]: data.whenToAdvanceUser,
    [DestinationConfigFormField.MAPPED_MATCH_FIELDS]: data.mappedMatchFields,
    [DestinationConfigFormField.PERSONALIZATION_FIELDS]:
      data.personalizationFields,
    [DestinationConfigFormField.ALL_PERSONALIZATION_FIELDS_SELECTED]:
      data.allPersonalizationFieldsSelected,
    [DestinationConfigFormField.ALL_GL_GENERATED_FIELDS_SELECTED]:
      data.allGLGeneratedFieldsSelected,
    [DestinationConfigFormField.MARKETO_AUDIENCE_TYPE]:
      data.marketo_audience_type,
    [DestinationConfigFormField.AUDIENCE_SCHEDULE]: data.schedule ?? null,
    [DestinationConfigFormField.TABLE_NAME]: data.tableName,
    [DestinationConfigFormField.USER_CONSENT]: data.userConsent,

    // Fields from MLBPrivacySettings
    [DestinationConfigFormField.LOSP]: data.privacySettings.losp,
    [DestinationConfigFormField.END_USER_OPT_IN]:
      data.privacySettings.end_user_opt_in,
    [DestinationConfigFormField.THIRD_PARTY_NAME]:
      data.privacySettings.third_party_name,
    [DestinationConfigFormField.CLUB_SPONSOR_ADVERTISER_IO]:
      data.privacySettings.club_sponsor_advertiser_io,
    [DestinationConfigFormField.OPT_IN_LANGUAGE_APPROVED_VETTED]:
      data.privacySettings.opt_in_language_approved_vetted,
    [DestinationConfigFormField.CLUB_SPONSOR_ADVERTISER_DATA_SHARE]:
      data.privacySettings.club_sponsor_advertiser_data_share,
    [DestinationConfigFormField.INFO_COLLECTED_VIA_MLB_PRIVACY_POLICY]:
      data.privacySettings.info_collected_via_mlb_privacy_policy,

    // Fields from ZetaFieldMappings
    [DestinationConfigFormField.VARIATION]: data.fieldMappings.variation,
    [DestinationConfigFormField.EMAIL_TYPE]: data.fieldMappings.email_type,
    [DestinationConfigFormField.CAMPAIGN_ID]: data.fieldMappings.campaign_id,
    [DestinationConfigFormField.EMAIL_LIST_ID]:
      data.fieldMappings.email_list_id,
    [DestinationConfigFormField.EXPORT_FREQUENCY]:
      data.fieldMappings.export_frequency,

    [DestinationConfigFormField.EXPORT_FIELDS]: data.exportFields,
    [DestinationConfigFormField.ADDITIONAL_FIELDS]: data.additionalFields,

    // Other fields
    isValid: data.isValid,
  };
}

export function unflattenConfigFormValues(
  data: ConfigFormValues
): ExportConfigFormData {
  return {
    schedule: data[DestinationConfigFormField.AUDIENCE_SCHEDULE] ?? null,
    platform_type: data[DestinationConfigFormField.PLATFORM_TYPE],
    exportName: data[DestinationConfigFormField.EXPORT_NAME],
    tableName: data[DestinationConfigFormField.TABLE_NAME],
    userConsent: data[DestinationConfigFormField.USER_CONSENT],
    duration: data[DestinationConfigFormField.DURATION],
    whenToAdvanceUser: data[DestinationConfigFormField.WHEN_TO_ADVANCE_USER],
    mappedMatchFields: data[DestinationConfigFormField.MAPPED_MATCH_FIELDS],
    personalizationFields:
      data[DestinationConfigFormField.PERSONALIZATION_FIELDS],
    additionalFields: data[DestinationConfigFormField.ADDITIONAL_FIELDS],
    allPersonalizationFieldsSelected:
      data[DestinationConfigFormField.ALL_PERSONALIZATION_FIELDS_SELECTED],
    allGLGeneratedFieldsSelected:
      data[DestinationConfigFormField.ALL_GL_GENERATED_FIELDS_SELECTED],
    marketo_audience_type:
      data[DestinationConfigFormField.MARKETO_AUDIENCE_TYPE],
    privacySettings: {
      losp: data[DestinationConfigFormField.LOSP] as string,
      end_user_opt_in: data[
        DestinationConfigFormField.END_USER_OPT_IN
      ] as boolean,
      third_party_name: data[
        DestinationConfigFormField.THIRD_PARTY_NAME
      ] as string,
      club_sponsor_advertiser_io: data[
        DestinationConfigFormField.CLUB_SPONSOR_ADVERTISER_IO
      ] as string,
      opt_in_language_approved_vetted: data[
        DestinationConfigFormField.OPT_IN_LANGUAGE_APPROVED_VETTED
      ] as boolean,
      club_sponsor_advertiser_data_share: data[
        DestinationConfigFormField.CLUB_SPONSOR_ADVERTISER_DATA_SHARE
      ] as string,
      info_collected_via_mlb_privacy_policy: data[
        DestinationConfigFormField.INFO_COLLECTED_VIA_MLB_PRIVACY_POLICY
      ] as boolean,
    },
    fieldMappings: {
      variation: data[DestinationConfigFormField.VARIATION] as string,
      email_type: data[DestinationConfigFormField.EMAIL_TYPE] as string,
      campaign_id: data[DestinationConfigFormField.CAMPAIGN_ID] as string,
      email_list_id: data[DestinationConfigFormField.EMAIL_LIST_ID] as string,
      export_frequency: data[
        DestinationConfigFormField.EXPORT_FREQUENCY
      ] as string,
    },
    isValid: data.isValid,
    exportFields: data.exportFields,
    tableNameValidation: {
      isDuplicate: false,
      hasInvalidCharacters: false,
    },
    dataExtensions: [],
  };
}

/**
 * Checks the current route to see if it is a journey route
 */
export function isJourneyRoute() {
  const {pathname} = window.location;
  if (pathname.includes('journey')) {
    if (pathname !== '/v2/journeys' && pathname !== '/journeys') {
      return true;
    }
  }
  return false;
}

function openManualModeConfirmationModal(
  journeyId: Nullable<number> | undefined,
  datasetGroupId: number,
  nodeId: string,
  direction: ManualModeDirection
) {
  const event = openModalEvent('manual-mode-confirmation');
  dispatchEvent(
    new CustomEvent(event, {
      detail: {
        journeyId,
        datasetGroupId,
        nodeId,
        direction,
      },
    })
  );
}

export function renderManualModeOptions(
  manualModeParams: Pick<
    ManualModeRunParams,
    'datasetGroupId' | 'journeyId' | 'nodeId'
  >,
  processing: boolean,
  journey: Partial<IJourney> | undefined
): TemplateResult {
  const journeyHasBeenPublished = journey?.status === JourneyStatus.PUBLISHED;
  const {datasetGroupId, journeyId, nodeId} = manualModeParams;
  const disableManualModeOption = () => {
    return (
      !journeyHasBeenPublished ||
      processing ||
      Object.values(journeyStore.attributes.getState().nodeMetadata).some(
        ({processing}) => !!processing
      )
    );
  };

  const template = html`
    <div style="display: flex; flex-direction: column; gap: 12px;">
      <fly-list-item
        .disabled=${disableManualModeOption()}
        @selected=${() => {
          if (!journeyHasBeenPublished || processing) {
            return;
          }
          openManualModeConfirmationModal(
            journeyId,
            datasetGroupId,
            nodeId,
            ManualModeDirection.UPSTREAM
          );
        }}
      >
        ${journeysLocale[locale.current].journeysTable.manualMode
          .runUntilThisNode}
      </fly-list-item>
      <fly-list-item
        .disabled=${disableManualModeOption()}
        @selected=${() => {
          openManualModeConfirmationModal(
            journeyId,
            datasetGroupId,
            nodeId,
            ManualModeDirection.SELF
          );
        }}
      >
        ${journeysLocale[locale.current].journeysTable.manualMode
          .runOnlyThisNode}
      </fly-list-item>
      <fly-list-item
        ?disabled=${disableManualModeOption()}
        @selected=${() => {
          openManualModeConfirmationModal(
            journeyId,
            datasetGroupId,
            nodeId,
            ManualModeDirection.DOWNSTREAM
          );
        }}
      >
        ${journeysLocale[locale.current].journeysTable.manualMode
          .runFromThisNode}
      </fly-list-item>
    </div>
  `;
  if (!journeyHasBeenPublished) {
    return wrapManualModeOptions(template);
  }
  return template;
}

export function wrapManualModeOptions(template: TemplateResult) {
  return html`
    <fly-tooltip content="Publish your journey before running manual mode">
      ${template}
    </fly-tooltip>
  `;
}

/** @deprecated */
export function isProcessingWithoutMetrics(
  processing: boolean,
  metrics: Nullable<NodeMetric>
) {
  return processing && !metrics;
}

export function showProcessingIndicator(
  status: Nullable<NodeMetricStatus>,
  journey: Partial<IJourney>
) {
  const isManualMode =
    journey.settings?.schedule?.type === JourneyScheduleType.MANUAL;
  const isPublished = journey.status === JourneyStatus.PUBLISHED;
  const isStopped = journey.status === JourneyStatus.STOPPED;

  switch (status) {
    case 'processing':
      return !isStopped;
    case 'completed':
      return false;
    default:
      if (isManualMode || isStopped) return false;
      return isPublished;
  }
}

export function getNodeMetrics<
  T extends {metrics: Nullable<NodeMetric>; processing: boolean},
>(model: T) {
  if (model.metrics?.status) return model.metrics.status;
  if (model.processing) return 'processing';
  return null;
}

export function isStudioJourney() {
  return location.href.includes('/v2/studio');
}

export function initializeRequiredFields(
  destination: DestinationConnection,
  exportAction = CampaignTriggersExportActions.ADD_TO_LIST_ONLY
) {
  if (!destination) return [];
  if (destination.is_mlb) {
    return [...BASE_REQUIRED_FIELDS, ...ADS_REQUIRED_FIELDS];
  }
  if (isWarehouseDestination(destination)) {
    return [...BASE_REQUIRED_FIELDS, ...DW_REQUIRED_FIELDS];
  }
  if (isEmailDestination(destination)) {
    let requiredFields = [...BASE_REQUIRED_FIELDS, ...EMAIL_REQUIRED_FIELDS];

    if (
      exportAction ===
      CampaignTriggersExportActions.ADD_TO_EXISTING_DATA_EXTENSION
    ) {
      requiredFields = [
        ...requiredFields,
        ...SFMC_ADD_TO_EXISTING_DATA_EXTENSION_REQUIRED_FIELDS,
      ];
    }

    if (destination.supports_campaign_triggers) {
      requiredFields = [
        ...requiredFields,
        ...CAMPAIGN_TRIGGERS_REQUIRED_FIELDS,
      ];
      if (SENDABLE_EXPORT_ACTIONS.includes(exportAction)) {
        requiredFields = [
          ...requiredFields,
          ...CAMPAIGN_TRIGGERS_SEND_EMAIL_REQUIRED_FIELDS,
        ];
        if (destination.campaign_triggers_choose_sender) {
          requiredFields = [
            ...requiredFields,
            ...CAMPAIGN_TRIGGERS_SEND_EMAIL_CHOOSE_SENDER_REQUIRED_FIELDS,
          ];
        }
      }
    }

    return requiredFields;
  }
  return [...BASE_REQUIRED_FIELDS, ...ADS_REQUIRED_FIELDS];
}

export function isWarehouseDestination(
  destination: DestinationConnection
): boolean {
  if (isUndefined(destination)) return false;

  return (
    destination.platform_type.includes('BigQuery') ||
    destination.platform_type.includes('Snowflake') ||
    destination.platform_type.includes('MessageGears')
  );
}

export function isEmailDestination(destination: DestinationConnection) {
  if (!destination) return false;
  if (!destination.categories) return false;
  return destination.categories.includes(ValidDestinationCategory.EMAIL);
}

export function isAdsDestination(destination: DestinationConnection) {
  return (
    destination?.categories?.length &&
    destination?.categories?.some(category => {
      return category === ValidDestinationCategory.ADS;
    })
  );
}

export function getTableNameValidationMessage(validation: TableNameValidation) {
  const currentJourneysLocale = journeysLocale[locale.current];
  if (validation.hasInvalidCharacters) {
    return currentJourneysLocale.paidMediaExport.tableNameValidation
      .invalidCharacters;
  }
  if (validation.isDuplicate) {
    return currentJourneysLocale.paidMediaExport.tableNameValidation
      .alreadyInUse;
  }
  return null;
}
